/**
 * Single source of truth for the dashboard's left sidebar.
 *
 * Every entry is a `route` (renders as a NavLink, can be the active
 * item). The `gate` callback receives the user role + config +
 * subscription visibility signals and returns whether the item should
 * appear at all.
 *
 * (Earlier versions also supported a `modal` kind for Reprocess /
 * Get-Links / CDN Prefetch — those have since been promoted to their
 * own pages so the sidebar surface is uniform.)
 *
 * Order matters: items render in the order declared per section.
 * Sections render in the order of `SECTION_ORDER`.
 *
 * The React analogue of `symlinks/filespin-customer-support-app/constants/nav.py`.
 */
import * as React from 'react';
import type { MessageDescriptor } from 'react-intl';

import type { FeatureId, FeatureStatus } from 'src/shared/features/types';

import {
  Bell,
  ImageSquare,
  Stack,
  ShareNetwork,
  ChartBar,
  ChartLineUp,
  ChatAI,
  ListBullets,
  ArrowsClockwise,
  AssetGetLink,
  Browser,
  ArrowCounterClockwise,
  Trash,
  TreeStructure,
  Info,
  Panel,
  Gear,
  SquaresFour,
  PlugsConnected,
  PuzzlePiece,
  CalendarClock,
  CloudArrowDown,
  Users,
  Checks,
  Eye,
} from 'src/common/icons';

import messages from './messages';

export type NavSection =
  | 'main'
  | 'workflows'
  | 'insights'
  | 'account'
  | 'advancedTools'
  | 'configure';

/** Render order of the sections in the nav list (top -> bottom). One rule,
 *  no sandwiching: the UNGROUPED content loop first, every TITLED group after
 *  (Workflows, Insights, Advanced tools, Configure). Mirrors the
 *  Stripe-dashboard pattern: flat shortcuts on top, grouped products below.
 *
 *  `account` is deliberately absent: its items render in the account menu
 *  pinned to the sidebar bottom (`AccountMenu`), not in this list.
 *
 *  `workflows` leads the titled groups because it is the only one of them
 *  that belongs to the everyday loop: it holds things pending on you or on
 *  someone else, so it gets checked as often as Assets does. */
export const SECTION_ORDER: NavSection[] = [
  'main',
  'workflows',
  'insights',
  'advancedTools',
  'configure',
];

/** Section title (the small uppercased heading above the items).
 *  Sections absent from this map render HEADERLESS — `main` because every
 *  role sees its items (a "Media" label said nothing). `account` is not in
 *  the nav list at all; the account menu labels it itself. */
export const SECTION_TITLES: Partial<Record<NavSection, MessageDescriptor>> = {
  workflows: messages.sectionWorkflows,
  insights: messages.sectionInsights,
  advancedTools: messages.sectionAdvancedTools,
  configure: messages.sectionConfigure,
};

/**
 * Inputs available to a nav-item's `gate` callback. Plumbed through by
 * `AppSidebar.tsx`. Add fields here when a new gating signal shows up
 * (e.g. a new feature flag); navItems will pick it up automatically.
 */
export interface NavGateCtx {
  isAdmin: boolean;
  isManager: boolean;
  isUser: boolean;
  /** Truthy when `useSubscriptionQuery` returned data (i.e. user can
   *  see the billing page). */
  subscriptionVisible: boolean;
  /** AI addon enabled (== `showChat` in NavBar.tsx terms). */
  aiAddonEnabled: boolean;
  /** Runtime per-feature availability (probed by
   *  `FeatureAvailabilityProvider`). Items carrying a `featureId` whose
   *  status is `unavailable` render visible-but-disabled. */
  featureAvailability: Record<FeatureId, FeatureStatus>;
  /** Various REACT_APP_* feature flags from `src/utils/config.ts`. */
  config: {
    showSubscription: boolean;
    showQueueStats: boolean;
    showTaskQueues: boolean;
    showDeliveryAnalytics: boolean;
    showMigration: boolean;
  };
}

interface BaseItem {
  id: string;
  section: NavSection;
  label: MessageDescriptor;
  icon: React.ComponentType<{ className?: string }>;
  gate: (ctx: NavGateCtx) => boolean;
  /** When set, the item is tied to a rollout feature: it stays visible but
   *  renders disabled ("enabling soon") whenever the feature's runtime
   *  availability is `unavailable`. */
  featureId?: FeatureId;
  /** Test selector used by Cypress / manual probes. Mirrors the
   *  `data-test` attribute previously on NavBar.tsx where applicable. */
  dataTest?: string;
}

export interface RouteNavItem extends BaseItem {
  kind: 'route';
  to: string;
  /** Paths used for the longest-prefix active-state match. If omitted,
   *  defaults to `[to]`. */
  match?: string[];
}

export type NavItem = RouteNavItem;

// ---------------------------------------------------------------------------
// Item list
// ---------------------------------------------------------------------------

export const navItems: NavItem[] = [
  // ----- Main (headerless) — the everyday content loop -------------------
  {
    id: 'assets',
    section: 'main',
    kind: 'route',
    to: '/assets',
    match: ['/assets', '/asset/'],
    label: messages.assets,
    icon: ImageSquare,
    gate: () => true,
    dataTest: 'assets-link',
  },
  {
    id: 'collections',
    section: 'main',
    kind: 'route',
    to: '/collections',
    match: ['/collections', '/collection/'],
    label: messages.collections,
    icon: Stack,
    gate: () => true,
    dataTest: 'collections-link',
  },
  {
    id: 'shares',
    section: 'main',
    kind: 'route',
    to: '/shares',
    match: ['/shares'],
    label: messages.shares,
    icon: ShareNetwork,
    gate: ({ isUser }) => !isUser,
    dataTest: 'shares-link',
  },

  // ----- Workflows ------------------------------------------------------
  // Things you started (or were asked to do) that are still in flight:
  // pending on you, or on someone else. Admission rules for this section —
  // and why campaign links deliberately fail them — are in
  // `docs/design/workflows-section.md`.
  //
  // Reviews (what I sent out) then Assigned to me (what is waiting on me) —
  // the same loop from opposite ends. Requester first: it is the surface you
  // visit deliberately, while the reviewer's half is what the badge drags you
  // to.
  {
    id: 'reviews',
    section: 'workflows',
    kind: 'route',
    to: '/workflows/reviews',
    match: ['/workflows/reviews'],
    label: messages.reviews,
    // Not ShareNetwork: that is Shares, three rows up, and two identical
    // icons in one rail is a worse signal than either alone. Eye reads as
    // "under review" against Checks ("settled") on the row below.
    icon: Eye,
    // Anyone who can start a review: creating one means sharing assets, which
    // is what `!isUser` already gates on Shares. A `user`-role viewer is out
    // of the review loop at both ends — see Assigned to me below.
    gate: ({ isUser }) => !isUser,
    dataTest: 'reviews-link',
  },
  {
    id: 'my-approvals',
    section: 'workflows',
    kind: 'route',
    to: '/workflows/my-approvals',
    match: ['/workflows/my-approvals'],
    label: messages.myApprovals,
    icon: Checks,
    // `!isUser`, same as Reviews above. This was `() => true` until
    // 2026-09-02, on the reasoning that a `user`-role viewer could be *asked*
    // to review even though they cannot send one. That asymmetry was the
    // mistake: the review loop is a distribution act end to end, and `user`
    // is the one role held out of it (it is why Shares is `!isUser`). A
    // viewer is not a reviewer, so this queue is not theirs.
    //
    // ⚠️ Half-done on purpose. The matching half — keeping `user`-role
    // teammates out of the reviewer picker in Send for review — is NOT in
    // this change, because it cannot be done client-side: that picker is fed
    // by `GET /api/v1/group/members`, whose payload deliberately omits role
    // (`to_json_directory` in `filespin/common/daos.py`), and the only
    // role-bearing list (`POST /api/v1/users`) is USER_ADMIN-only while
    // manager and creator can also send reviews. So a requester can still
    // pick a viewer, who then gets the task row and the email with no page
    // in the rail to act on it — they can still resolve it from
    // /notifications, where the `tasks` category remains visible to every
    // role. Closing that needs role on the group directory; until then do
    // not "fix" this by gating the picker on `isAdmin`, which would only
    // make the behaviour depend on who is sending.
    gate: ({ isUser }) => !isUser,
    dataTest: 'my-approvals-link',
  },
  {
    id: 'ai-schedules',
    section: 'workflows',
    kind: 'route',
    // Route deliberately unchanged. `SCHEDULES_PAGE_PATH` in
    // `filespin/core/ai_agent_schedules.py` is baked into every schedule
    // notification link (including mail already sitting in inboxes), so
    // moving this to /workflows/ai-schedules is a two-repo change for
    // cosmetic gain. The section move is the part that carries meaning.
    to: '/ai/schedules',
    match: ['/ai/schedules'],
    label: messages.aiSchedules,
    icon: CalendarClock,
    // Moved here from `configure` (2026-09-01). A schedule is not a setting:
    // it is a running thing with a history you inspect — there is a run
    // replay page, and you do not replay a configuration. The old filing
    // also meant an auto-paused schedule (a live incident) was announced
    // from a section nobody visits between setup and breakage.
    //
    // Built-in feature: always shown and clickable for admin/manager. The
    // page itself renders read-only ("Not enabled") when the AI addon isn't
    // enabled or the backend API is unavailable, rather than being disabled
    // here in the sidebar.
    gate: ({ isAdmin, isManager }) => isAdmin || isManager,
  },

  // ----- Insights -------------------------------------------------------
  {
    id: 'usage',
    section: 'insights',
    kind: 'route',
    to: '/usage',
    match: ['/usage'],
    label: messages.overview,
    icon: ChartBar,
    gate: ({ isAdmin }) => isAdmin,
    dataTest: 'usage-link',
  },
  {
    id: 'ai-usage',
    section: 'insights',
    kind: 'route',
    to: '/insights/ai-usage',
    match: ['/insights/ai-usage'],
    label: messages.aiUsage,
    icon: ChatAI,
    // Built-in feature: always shown to admin/manager regardless of the AI
    // addon. The page renders read-only (all sections visible, controls
    // disabled) when the addon isn't enabled or the backend API 404s.
    gate: ({ isAdmin, isManager }) => isAdmin || isManager,
    dataTest: 'ai-usage-link',
  },
  // ui#978. What agents did with your access. Every role has activity of
  // its own, so every role sees it; admins get a whole-account view on the
  // page itself.
  {
    id: 'agent-activity',
    section: 'insights',
    kind: 'route',
    to: '/insights/agent-activity',
    match: ['/insights/agent-activity'],
    label: messages.agentActivity,
    icon: ListBullets,
    gate: () => true,
    dataTest: 'agent-activity-link',
  },
  // Phase 5b. Subscription-scoped CDN delivery analytics. Admin/manager only,
  // and gated behind the REACT_APP_SHOW_DELIVERY_ANALYTICS flag. When visible,
  // the page renders read-only (content visible, actions disabled) via its
  // runtime availability check whenever the backend API 404s.
  {
    id: 'delivery-analytics',
    section: 'insights',
    kind: 'route',
    to: '/insights/delivery',
    match: ['/insights/delivery'],
    label: messages.deliveryAnalytics,
    icon: ChartLineUp,
    gate: ({ isAdmin, isManager, config }) =>
      (isAdmin || isManager) && config.showDeliveryAnalytics,
    dataTest: 'delivery-analytics-link',
  },

  // Notifications rides with the content loop: every role sees it, and a
  // one-item "Activity" section was a header for nothing.
  {
    id: 'notifications',
    section: 'main',
    kind: 'route',
    to: '/notifications',
    match: ['/notifications'],
    label: messages.notifications,
    icon: Bell,
    gate: () => true,
    dataTest: 'notifications-link',
  },
  {
    id: 'jobs',
    section: 'advancedTools',
    kind: 'route',
    to: '/jobs',
    match: ['/jobs', '/job/'],
    label: messages.jobs,
    icon: ListBullets,
    gate: ({ isAdmin, isManager }) => isAdmin || isManager,
  },
  {
    id: 'task-queues',
    section: 'advancedTools',
    kind: 'route',
    // Renamed from /admin/workflows-and-tasks (2026-09-01). The label and the
    // component (`ProcessingQueues`) always agreed; only the route said
    // "workflows". Harmless until the Workflows section existed — now it would
    // send an admin following a "workflows" URL to Celery queue depths. The old
    // path still redirects here (see Router.tsx).
    to: '/admin/processing-queues',
    match: ['/admin/processing-queues', '/admin/workflows-and-tasks'],
    label: messages.taskQueues,
    icon: Info,
    gate: ({ isAdmin, isManager, config }) =>
      (isAdmin || isManager) && config.showTaskQueues,
    dataTest: 'processing-queues',
  },

  // ----- Advanced tools (admin/manager; hidden for other roles) -----------
  // One-off operational tools + the processing monitors (Jobs, Task
  // Queues — declared above so they render first here). Every item is
  // admin/manager-gated, so the whole section disappears for creators
  // and viewers.
  {
    id: 'reprocess',
    section: 'advancedTools',
    kind: 'route',
    to: '/reprocess-assets',
    label: messages.reprocessAssets,
    icon: ArrowsClockwise,
    gate: ({ isAdmin, isManager }) => isAdmin || isManager,
    dataTest: 'reprocess-assets',
  },
  {
    id: 'getlink',
    section: 'advancedTools',
    kind: 'route',
    to: '/get-asset-links',
    label: messages.getAssetLinks,
    icon: AssetGetLink,
    // Was `() => true` (any logged-in role, inherited from the old
    // NavBar's `getToolsOptions`). Tightened to match the section: batch
    // link generation is an operational tool, and one visible item would
    // otherwise resurrect "Advanced tools" for viewers and creators.
    gate: ({ isAdmin, isManager }) => isAdmin || isManager,
    dataTest: 'get-asset-links',
  },
  {
    id: 'cdn-prefetch',
    section: 'advancedTools',
    kind: 'route',
    to: '/cdn-prefetch-purge',
    label: messages.cdnPrefetch,
    icon: Browser,
    gate: ({ isAdmin, isManager }) => isAdmin || isManager,
    dataTest: 'cdn-prefetch-purge',
  },
  {
    id: 'reissue-callbacks',
    section: 'advancedTools',
    kind: 'route',
    to: '/reissue-callbacks',
    label: messages.reissueCallbacks,
    icon: ArrowCounterClockwise,
    gate: ({ isAdmin, isManager }) => isAdmin || isManager,
    dataTest: 'reissue-callbacks',
  },
  {
    id: 'bulk-purge',
    section: 'advancedTools',
    kind: 'route',
    to: '/bulk-purge-assets',
    label: messages.bulkPurge,
    icon: Trash,
    gate: ({ isAdmin }) => isAdmin,
    dataTest: 'bulk-delete',
  },

  // ----- Account — the admin pair: people, then money --------------------
  // Rendered under "Your organization" in the sidebar-bottom account menu
  // (`AccountMenu`), not in the nav list. The gates still live here so every
  // sidebar gate is in one tested place.
  {
    id: 'users',
    section: 'account',
    kind: 'route',
    to: '/admin/users',
    label: messages.users,
    icon: Users,
    gate: ({ isAdmin }) => isAdmin,
    dataTest: 'users-link',
  },
  {
    id: 'subscription',
    section: 'account',
    kind: 'route',
    to: '/admin/subscription',
    label: messages.subscription,
    icon: Panel,
    // `subscriptionVisible` already implies admin (AppSidebar only fires the
    // query for admins). Subscription is an admin-only surface, so we keep
    // `isAdmin` explicit here. Note: /api/v1/subscription now returns 403
    // (not 401) for authenticated non-admins, so firing it for them would no
    // longer be interpreted as a session expiry / log the user out — the
    // admin gate is a product/scoping choice, not a logout workaround.
    gate: ({ isAdmin, config, subscriptionVisible }) =>
      isAdmin && config.showSubscription && subscriptionVisible,
    dataTest: 'subscription-link',
  },

  // ----- Configure ------------------------------------------------------
  {
    id: 'settings',
    section: 'configure',
    kind: 'route',
    // SettingsRedirect (admin/settings/) picks the right first tab.
    to: '/admin/settings/',
    match: ['/admin/settings'],
    label: messages.settings,
    icon: Gear,
    gate: ({ isAdmin, isManager }) => isAdmin || isManager,
    dataTest: 'settings-link',
  },
  {
    id: 'schemas',
    section: 'configure',
    kind: 'route',
    to: '/admin/schemas',
    label: messages.assetSchemas,
    icon: TreeStructure,
    gate: ({ isAdmin, isManager }) => isAdmin || isManager,
  },
  {
    id: 'share-templates',
    section: 'configure',
    kind: 'route',
    to: '/admin/shares/templates',
    label: messages.sharePresets,
    icon: SquaresFour,
    gate: ({ isAdmin, isManager }) => isAdmin || isManager,
  },
  {
    id: 'addons',
    section: 'configure',
    kind: 'route',
    to: '/admin/addons',
    label: messages.addons,
    icon: PuzzlePiece,
    gate: ({ isAdmin, isManager }) => isAdmin || isManager,
  },
  // Migrating an account off Cloudinary / ImageKit. Its own Configure page
  // rather than a Settings tab: the strip is "how FileSpin itself behaves" and
  // is admin+manager, while this absorbs ANOTHER vendor's URLs, is a one-off
  // project, and is admin-only (the endpoints 403 everyone else). Behind
  // REACT_APP_SHOW_MIGRATION because the page is a dead end without the
  // migration-onramp service; `featureId` additionally greys it out when that
  // service answers 503 on a stack where the flag is on.
  {
    id: 'migrate',
    section: 'configure',
    kind: 'route',
    to: '/admin/migrate',
    match: ['/admin/migrate'],
    label: messages.migrate,
    icon: CloudArrowDown,
    featureId: 'migration',
    gate: ({ isAdmin, config }) => isAdmin && config.showMigration,
    dataTest: 'migrate-link',
  },
  // Push destinations (GA4/Segment) for CDN delivery analytics — admin-only
  // account config, gated behind the REACT_APP_SHOW_DELIVERY_ANALYTICS flag.
  // When visible, the page renders read-only (content visible, actions
  // disabled) via its runtime availability check whenever the backend 404s.
  {
    id: 'analytics-destinations',
    section: 'configure',
    kind: 'route',
    to: '/insights/destinations',
    match: ['/insights/destinations'],
    label: messages.analyticsDestinations,
    icon: PlugsConnected,
    gate: ({ isAdmin, config }) => isAdmin && config.showDeliveryAnalytics,
    dataTest: 'analytics-destinations-link',
  },
];

/**
 * Longest-prefix matcher. Mirrors the algorithm in
 * `symlinks/filespin-customer-support-app/templates/__init__.py`
 * (`_active_nav_id`): the item whose longest `match` entry is a prefix
 * of `pathname` wins.
 */
export function resolveActiveNavId(items: NavItem[], pathname: string): string | null {
  let bestId: string | null = null;
  let bestLength = 0;

  for (const item of items) {
    const candidates = item.match && item.match.length > 0 ? item.match : [item.to];
    for (const candidate of candidates) {
      if (!candidate) continue;
      // Strict prefix match: pathname must equal the candidate or have
      // the candidate followed by a `/`. Avoids `/jobs` matching
      // `/jobseekers`.
      const matches =
        pathname === candidate ||
        pathname.startsWith(candidate.endsWith('/') ? candidate : `${candidate}/`);
      if (matches && candidate.length > bestLength) {
        bestId = item.id;
        bestLength = candidate.length;
      }
    }
  }

  return bestId;
}
